import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { isJpg, isPng } from '@client/helpers/imageHelper';

// Hooks
import { useDropzone } from 'react-dropzone';

// Components
import uploadIcon from '@client/shared/images/upload.svg';
import Image from '@client/components/image/Image';

// Constants
import {
  ERROR_REJECTED_IMAGE,
  ERROR_CORRUPT_IMAGE,
} from '@client/definitions/errorMessages';
import {
  IMAGE_SIZE_RECOMMENDATIONS,
  IMAGE_UPLOADER_CROP,
  IMAGE_ALLOWED_TYPES,
  IMAGE_MAX_SIZE,
  IMAGE_DRAG_AND_DROP,
  IMAGE_OR,
  IMAGE_SELECT,
  IMAGE_CANCEL,
} from '@client/definitions/constants';

// Styles
import styleHelper from '@client/shared/styles/styleHelper.scss';
import styles from './dropzone.scss';

export const Dropzone = ({ setPreviewImage, handleReset, setCurrentView }) => {
  const [error, setError] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles?.forEach((file) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const buffer = e?.target?.result;
        const uint8Array = buffer && new Uint8Array(buffer);
        const isJpgOrPng = isJpg(uint8Array) || isPng(uint8Array);

        handleSelectedFile(isJpgOrPng, file);
      };

      reader.readAsArrayBuffer(file);
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: 2 * 1024 * 1024,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png'] },
    onDropRejected: () => setError(ERROR_REJECTED_IMAGE),
  });

  const handleSelectedFile = (isValid, file) => {
    if (!isValid) setError(ERROR_CORRUPT_IMAGE);
    if (isValid) {
      setError(false);
      handleImageDrop(file);
    }
  };

  const handleImageDrop = (file) => {
    const previewUrl = URL.createObjectURL(file);
    setPreviewImage(previewUrl);
    setCurrentView(IMAGE_UPLOADER_CROP);
  };

  return (
    <>
      <div className={styles.dropzone} {...getRootProps()}>
        <input {...getInputProps()} />
        <Image className={styles.icon} src={uploadIcon} width={60} />
        <p className={styles.primaryText}>{IMAGE_DRAG_AND_DROP}</p>
        <p className={styles.smallText}>{IMAGE_OR}</p>
        <button
          className={`${styleHelper.btnPrimaryMd} ${styles.btn}`}
          type="button"
          aria-label={IMAGE_SELECT}
        >
          {IMAGE_SELECT}
        </button>
        <ul className={styles.list}>
          <li className={styles.listItem}>{IMAGE_SIZE_RECOMMENDATIONS}</li>
          <li className={styles.listItem}>{IMAGE_ALLOWED_TYPES}</li>
          <li className={styles.listItem}>{IMAGE_MAX_SIZE}</li>
        </ul>
      </div>

      {error && <span className={styles.errorText}>{error}</span>}

      <div className={`${styles.btnContainer}`}>
        <button
          className={`${styleHelper.btnPrimaryMd} ${styles.btn}`}
          type="button"
          onClick={() => handleReset()}
          aria-label={IMAGE_CANCEL}
        >
          {IMAGE_CANCEL}
        </button>
      </div>
    </>
  );
};

Dropzone.propTypes = {
  setPreviewImage: PropTypes.func,
  handleReset: PropTypes.func,
  setCurrentView: PropTypes.func,
};
